import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import './LabelEdit.css';

const LabelEdit = () => {
  const { id } = useParams();
  const token = localStorage.getItem('token');

  const [foreignPrefilled, setForeignPrefilled] = useState(false);
  const [formData, setFormData] = useState({
    foreign_key_model: '',
    foreign_key_id: '',
    file_path: '',
    datasets: '',
    annotator: '',
    frame_end_systole: '',
    frame_end_diastole: '',
    frame_random: '',
    side: 'Unknown',
    frame_initial_contrast_appearance: '',
    frame_start_washout: '',
    frame_end_washout: '',
    comments: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Fetch existing label data
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/patients/fsa_labels/${id}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => {
        const label = res.data;
        setFormData({
          foreign_key_model: label.foreign_key.modality,
          foreign_key_id: label.foreign_key.id,
          file_path: label.file_path,
          datasets: label.datasets ? label.datasets.join(', ') : '',
          annotator: label.annotator,
          frame_end_systole: label.frame_end_systole || '',
          frame_end_diastole: label.frame_end_diastole || '',
          frame_random: label.frame_random || '',
          side: label.side || 'Unknown',
          frame_initial_contrast_appearance: label.frame_initial_contrast_appearance || '',
          frame_start_washout: label.frame_start_washout || '',
          frame_end_washout: label.frame_end_washout || '',
          comments: label.comments || '',
        });
        setForeignPrefilled(true);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Error fetching label for editing.');
        setLoading(false);
      });
  }, [id, token]);

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Submit the edited data
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare payload similar to the create form
    const payload = {
      foreign_key: {
        modality: formData.foreign_key_model,
        id: parseInt(formData.foreign_key_id, 10),
      },
      file_path: formData.file_path,
      datasets: formData.datasets
        ? formData.datasets.split(',').map((dataset) => dataset.trim())
        : [],
      annotator: formData.annotator,
      frame_end_systole: formData.frame_end_systole ? parseInt(formData.frame_end_systole, 10) : null,
      frame_end_diastole: formData.frame_end_diastole ? parseInt(formData.frame_end_diastole, 10) : null,
      frame_random: formData.frame_random ? parseInt(formData.frame_random, 10) : null,
      side: formData.side,
      frame_initial_contrast_appearance: formData.frame_initial_contrast_appearance ? parseInt(formData.frame_initial_contrast_appearance, 10) : null,
      frame_start_washout: formData.frame_start_washout ? parseInt(formData.frame_start_washout, 10) : null,
      frame_end_washout: formData.frame_end_washout ? parseInt(formData.frame_end_washout, 10) : null,
      comments: formData.comments || null,
    };

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/patients/fsa_labels/${id}/edit/`,
        payload,
        { headers: { Authorization: `Token ${token}` } }
      );
      setSuccess(true);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error updating label. Please try again.');
      setSuccess(false);
    }
  };

  if (loading) return <p>Loading Label...</p>;
  if (error && !success) return <p className="error-message">{error}</p>;

  return (
    <div className="label-edit-container">
      <h3>Edit Label</h3>
      {error && <p className="error-message">{error}</p>}
      {success && <p className="success-message">Label updated successfully!</p>}

      <form onSubmit={handleSubmit} className="label-edit-form">
        <div className="form-group">
          <label>Modality <span className="required-asterisk">*</span></label>
          <input
            type="text"
            name="foreign_key_model"
            value={formData.foreign_key_model}
            onChange={handleChange}
            required
            disabled={foreignPrefilled}
          />
        </div>
        <div className="form-group">
          <label>Foreign Key ID <span className="required-asterisk">*</span></label>
          <input
            type="text"
            name="foreign_key_id"
            value={formData.foreign_key_id}
            onChange={handleChange}
            required
            disabled={foreignPrefilled}
          />
        </div>
        <div className="form-group">
          <label>File Path <span className="required-asterisk">*</span></label>
          <input
            type="text"
            name="file_path"
            value={formData.file_path}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Datasets</label>
          <input
            type="text"
            name="datasets"
            value={formData.datasets}
            onChange={handleChange}
            placeholder="Comma separated datasets, e.g. 'dataset1, dataset2'"
          />
        </div>
        <div className="form-group">
          <label>Annotator <span className="required-asterisk">*</span></label>
          <input
            type="text"
            name="annotator"
            value={formData.annotator}
            onChange={handleChange}
            required
          />
        </div>
        
        <h4>Frame Information</h4>
        <div className="form-group">
          <label>Frame End Systole</label>
          <input
            type="number"
            name="frame_end_systole"
            value={formData.frame_end_systole}
            onChange={handleChange}
            placeholder="Frame number for end of systole"
            min="0"
          />
        </div>
        <div className="form-group">
          <label>Frame End Diastole</label>
          <input
            type="number"
            name="frame_end_diastole"
            value={formData.frame_end_diastole}
            onChange={handleChange}
            placeholder="Frame number for end of diastole"
            min="0"
          />
        </div>
        <div className="form-group">
          <label>Frame Random</label>
          <input
            type="number"
            name="frame_random"
            value={formData.frame_random}
            onChange={handleChange}
            placeholder="Random frame number"
            min="0"
          />
        </div>
        
        <div className="form-group">
          <label>Side</label>
          <select
            name="side"
            value={formData.side}
            onChange={handleChange}
          >
            <option value="Unknown">Unknown</option>
            <option value="LCA">LCA</option>
            <option value="RCA">RCA</option>
          </select>
        </div>
        
        <h4>Contrast Information</h4>
        <div className="form-group">
          <label>Frame Initial Contrast Appearance</label>
          <input
            type="number"
            name="frame_initial_contrast_appearance"
            value={formData.frame_initial_contrast_appearance}
            onChange={handleChange}
            placeholder="Frame number for initial contrast appearance"
            min="0"
          />
        </div>
        <div className="form-group">
          <label>Frame Start Washout</label>
          <input
            type="number"
            name="frame_start_washout"
            value={formData.frame_start_washout}
            onChange={handleChange}
            placeholder="Frame number for start of washout"
            min="0"
          />
        </div>
        <div className="form-group">
          <label>Frame End Washout</label>
          <input
            type="number"
            name="frame_end_washout"
            value={formData.frame_end_washout}
            onChange={handleChange}
            placeholder="Frame number for end of washout"
            min="0"
          />
        </div>
        
        <div className="form-group">
          <label>Comments</label>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            placeholder="Additional comments or notes"
            rows="4"
          />
        </div>
        
        <button type="submit" className="label-edit-btn">
          Update Label
        </button>
      </form>
    </div>
  );
};

export default LabelEdit;
